import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { localize } from '@saviynt/common';
import {
  Badge,
  Box,
  Button,
  ButtonIcon,
  Checkbox,
  Icon,
  MobileDrawer,
  Popover,
  Tooltip,
  Typography,
} from '@saviynt/design-system';
import classnames from 'classnames';
import moment from 'moment';

import {
  SCREEN_SIZE_MOBILE_XLARGE,
  SCREEN_WIDTH_QUERIES,
  SYSTEM_DATA_THEMES,
} from '../../../utils/constant';
import { executeBackNavigation, getMessageKey } from '../../../utils/utils';
import CertsExport from '../CertsExport/CertsExport';
import CertsPersonalization from '../CertsPersonalization/CertsPersonalization';

import CertsDiscoveryCenter from './CertsDiscoveryCenter';

import './CertsHeader.css';

const TOOLTIP_DELAY_IN_MS_TILE = 500;

function CertsHeader() {
  const intl = useIntl();
  const [isOpenViewOption, setIsOpenViewOption] = useState(false);
  const [isCertHeaderTextHovered, setIsCertHeaderTextHovered] = useState(false);
  const [isMobileDrawerOpen, setIsMobileDrawerOpen] = useState(false);
  const [showKebabMenu, setShowKebabMenu] = useState(false);
  const [isPopoverOpen, setPopoverOpen] = useState(true);
  const [isCheckboxChecked, setCheckboxChecked] = useState(false);
  const LOCAL_STORAGE_KEY = 'personalizationCertsKeys';

  const { messages } = useSelector((state) => state?.user);
  const {
    global: {
      backNavigation,
      certificationName,
      certKey,
      mediaQueryValue,
      globalDateFormat,
    },
    lineActions: { certificationEndDate, reassignConfigMap } = {},
    personalization,
  } = useSelector((state) => state?.certifications?.details);

  const { isSecondaryCertifierLoggedIn } = reassignConfigMap;

  const { viewOption, clearCurrentPersonalization, status } = personalization;

  const mobileXlargeQuery = SCREEN_WIDTH_QUERIES.find(
    (item) => item.label === SCREEN_SIZE_MOBILE_XLARGE
  );
  const isMobileXlargeWidth = mobileXlargeQuery.value <= mediaQueryValue;

  const formatCertificationEndDate = (date) =>
    moment.utc(date).local().format(`${globalDateFormat?.toUpperCase()}`);

  const certificationDueText =
    messages.CERTS_CERTIFICTION_DUE ||
    localize(intl, getMessageKey('CERTS_CERTIFICTION_DUE'));
  const certificationDueTextMobile =
    messages.CERTS_CERTIFICTION_DUE_MOBILE ||
    localize(intl, getMessageKey('CERTS_CERTIFICTION_DUE_MOBILE'));

  const getCertificationEndDateText = (date, isMobile) => {
    const formattedDate = formatCertificationEndDate(date);

    if (!formattedDate) return '';

    const label = isMobile
      ? `${certificationDueTextMobile}: `
      : `${certificationDueText}: `;

    return `${label}${formattedDate}`;
  };

  const certificationEndDateText = getCertificationEndDateText(
    certificationEndDate,
    isMobileXlargeWidth
  );

  // Close Drawer on breakpoint resize
  useEffect(() => {
    if (!isMobileXlargeWidth) setIsMobileDrawerOpen(false);
  }, [isMobileXlargeWidth]);

  const certsHeaderClasses = classnames(
    'CertsHeader',
    isOpenViewOption && 'CertsHeader--isOpenViewOption',
    isCertHeaderTextHovered && 'CertsHeader--isOpenViewOption'
  );

  const handleMouseEnter = () => setIsCertHeaderTextHovered(true);
  const handleMouseLeave = () => setIsCertHeaderTextHovered(false);

  useEffect(() => {
    setIsMobileDrawerOpen(false);
  }, [viewOption?.view, clearCurrentPersonalization, status]);

  useEffect(() => {
    if (
      status === 'EXIST_PERSONALIZATION' ||
      status === 'UPDATE_PERSONALIZATION'
    ) {
      setShowKebabMenu(true);
    }
  }, [status]);

  const getCertKeysFromLocalStorage = () => {
    const certKeys = localStorage.getItem(LOCAL_STORAGE_KEY);

    return certKeys ? JSON.parse(certKeys) : [];
  };

  const saveCertKeyToLocalStorage = (key) => {
    const certKeys = getCertKeysFromLocalStorage();

    if (!certKeys.includes(key)) {
      localStorage.setItem(
        LOCAL_STORAGE_KEY,
        JSON.stringify([...certKeys, key])
      );
    }
  };

  const doesCertKeyExist = () =>
    getCertKeysFromLocalStorage().includes(certKey);

  useEffect(() => {
    if (doesCertKeyExist()) {
      setPopoverOpen(false);
    }
  }, [certKey]);

  const handleClose = () => {
    if (isCheckboxChecked) {
      saveCertKeyToLocalStorage(certKey);
    }

    setPopoverOpen(false);
  };

  const handleCheckbox = (event) => {
    setCheckboxChecked(event.target.checked);
  };

  const viewOptionsLabel =
    messages.PERSONALIZATION_VIEW_OPTIONS ||
    localize(intl, getMessageKey('PERSONALIZATION_VIEW_OPTIONS'));
  const popoverText =
    messages.PERSONALIZATION_POOPOVER_MESSAGE ||
    localize(intl, getMessageKey('PERSONALIZATION_POOPOVER_MESSAGE'));
  const checkboxText =
    messages.PERSONALIZATION_POOPOVER_CHECKBOX_MESSAGE ||
    localize(intl, getMessageKey('PERSONALIZATION_POOPOVER_CHECKBOX_MESSAGE'));

  const personalizationPopoverContent = () => (
    <Box className='CertsHeader-popover' dataTheme={SYSTEM_DATA_THEMES.DARK}>
      <Box
        className='CertsHeader-popoverHeader'
        dataTheme={SYSTEM_DATA_THEMES.LIGHT}>
        <Typography kind='h3'>{viewOptionsLabel}</Typography>
        <ButtonIcon
          dataTheme={SYSTEM_DATA_THEMES.LIGHT}
          size='small'
          kind='ghost'
          onClick={() => handleClose()}
          icon={<Icon kind='Close' color='neutral-1000' size='small' />}
        />
      </Box>
      <Box
        className='CertsHeader-popoverBody'
        dataTheme={SYSTEM_DATA_THEMES.LIGHT}>
        <Typography kind='body2'>{popoverText}</Typography>

        <Checkbox
          id='simple-text-certs-header-checkbox'
          name='simpleText'
          isChecked={isCheckboxChecked}
          onChange={handleCheckbox}
          label={checkboxText}
          textPosition='after'
          showHoverEffect
          dataTestId='simpleText'
        />
      </Box>

      <Box
        className='CertsHeader-popoverButton'
        dataTheme={SYSTEM_DATA_THEMES.LIGHT}>
        <Button type='button' kind='filled' size='small' onClick={handleClose}>
          {messages.CERTS_REJECT_SECONDARY_BTN ||
            localize(intl, getMessageKey('CERTS_REJECT_SECONDARY_BTN'))}
        </Button>
      </Box>
    </Box>
  );

  const kebabMenuWithPopover = () => (
    <Box className='CertsHeader-kebabMenu'>
      {status === 'UPDATE_PERSONALIZATION' ? <Badge size='small' /> : null}
      <Popover
        dataTheme={SYSTEM_DATA_THEMES.LIGHT}
        trigger={
          <ButtonIcon
            dataTheme={SYSTEM_DATA_THEMES.DARK}
            size='small'
            kind='ghost'
            onClick={() => {
              setIsMobileDrawerOpen(true);
            }}
            icon={
              <Icon
                kind='MoreVertical'
                color='neutral-000'
                size='smallMedium'
              />
            }
          />
        }
        popoverContent={personalizationPopoverContent()}
        isOpen={isPopoverOpen}
        setIsOpen={() => {}}
      />
    </Box>
  );

  return (
    <Box
      dataTheme='dark'
      tag='header'
      className={certsHeaderClasses}
      dataTestId='certs-header'>
      <div
        className='CertsHeader-textWrapper'
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}>
        <Tooltip
          openDelay={TOOLTIP_DELAY_IN_MS_TILE}
          text={certificationName}
          maxWidth='20rem'
          placement='bottom-start'
          offset={24}
          arrowStaticOffset='16'
          trigger={
            <div className='CertsHeader-textContainer'>
              <Typography kind='h2' mobile='h3-m' className='CertsHeader-text'>
                {certificationName || ''}
              </Typography>
              <Typography
                kind='body2'
                mobile='h4-m'
                className='CertsHeader-text'>
                {(certificationEndDate && certificationEndDateText) || ''}
              </Typography>
            </div>
          }
        />
      </div>
      <Box tag='div' className='CertsHeader-functionGroup'>
        <Box className='CertsHeader-controls'>
          {/* For the screen >600px */}
          <div className='CertsHeader-containerGreaterThan600'>
            <CertsPersonalization
              isOpenViewOption={isOpenViewOption}
              setIsOpenViewOption={setIsOpenViewOption}
            />
            {!backNavigation?.consulteeLoggedIn ||
            (backNavigation?.consulteeLoggedIn &&
              isSecondaryCertifierLoggedIn) ? (
              <CertsExport />
            ) : (
              ''
            )}
          </div>

          {/* For the screen <600px */}
          {showKebabMenu ? (
            <div className='CertsHeader-containerSmallerThan600'>
              {kebabMenuWithPopover()}
              <MobileDrawer
                isOpen={isMobileDrawerOpen}
                setIsOpen={setIsMobileDrawerOpen}
                isSwipeEnabled
                content={
                  <CertsPersonalization
                    isOpenViewOption={isOpenViewOption}
                    setIsOpenViewOption={setIsOpenViewOption}
                    isOnMobile={isMobileDrawerOpen}
                  />
                }
              />
            </div>
          ) : null}

          {isMobileXlargeWidth ? null : <CertsDiscoveryCenter />}

          <ButtonIcon
            size={isMobileXlargeWidth ? 'small' : 'large'}
            kind='ghost'
            icon={<Icon kind='Close' />}
            onClick={() => executeBackNavigation(backNavigation)}
            className='CertsHeader-closeBtn'
          />
        </Box>
      </Box>
    </Box>
  );
}

export default CertsHeader;
